import { urls as LightningUrls } from 'lightning-common/lib/configuration';
import { MessageKey } from 'lightning-messages/lib/messages';
import { ReactNode } from 'react';

import { getHtmlCopy } from '../../i18n';
import { List } from '../common/List';
import { ListItem } from '../common/Listitem';
import { Icon, IconName } from '../icon';
import { LogoKindleWhite } from '../logos';
import { Message } from '../utils/Message';
import { FooterColumn } from './FooterColumn';

import './FooterPromos.scss';

interface FooterPromoProps {
    children: ReactNode;
    logo: ReactNode;
    tagLine: MessageKey;
}

const FooterPromo = ({ children, logo, tagLine }: FooterPromoProps) => (
    <div className="footer__promo">
        {logo}

        <p className="footer__promo-tag-line">
            <Message messageKey={tagLine} />
        </p>

        {children}
    </div>
);

interface FooterKindlePromoProps {
    openInNewWindow?: boolean;
}

const FooterKindlePromo = ({ openInNewWindow }: FooterKindlePromoProps) => (
    <FooterPromo
        logo={<LogoKindleWhite />}
        tagLine={'footer.kindleReadAnywhereSentance'}
    >
        <List
            className="footer__promo-icon-links"
            heading={getHtmlCopy('kindle.brandName')}
        >
            <DeviceIconLink
                href={LightningUrls.kindleUrl}
                iconName="android"
                label={getHtmlCopy('googlePlayStore')}
                openInNewWindow={openInNewWindow}
            />
            <DeviceIconLink
                href={LightningUrls.kindleUrl}
                iconName="apple"
                label={getHtmlCopy('appleAppStore')}
                openInNewWindow={openInNewWindow}
            />
            <DeviceIconLink
                href={LightningUrls.kindleUrl}
                iconName="windows"
                label={getHtmlCopy('windowsStore')}
                openInNewWindow={openInNewWindow}
            />
        </List>
    </FooterPromo>
);

interface DeviceIconLinkProps {
    gaAction?: string;
    href: string;
    iconName: IconName;
    label: string;
    openInNewWindow?: boolean;
}

export const DeviceIconLink = ({
    gaAction,
    href,
    iconName,
    label,
    openInNewWindow,
}: DeviceIconLinkProps) => (
    <ListItem>
        <a
            aria-label={label}
            data-ga-action={gaAction}
            href={href}
            rel={openInNewWindow ? 'noreferrer' : undefined}
            target={openInNewWindow ? '_blank' : undefined}
        >
            <Icon classes="footer__promo-device-icon" name={iconName} />
        </a>
    </ListItem>
);

interface FooterPromoColumnProps {
    children: ReactNode;
    logo: ReactNode;
    openInNewWindow?: boolean;
    showKindlePromo?: boolean;
    tagLine: MessageKey;
}

export const FooterPromoColumn = ({
    children,
    logo,
    openInNewWindow,
    showKindlePromo,
    tagLine,
}: FooterPromoColumnProps) => (
    <FooterColumn classes="footer__promo-column">
        <FooterPromo logo={logo} tagLine={tagLine}>
            {children}
        </FooterPromo>

        {showKindlePromo && (
            <FooterKindlePromo openInNewWindow={openInNewWindow} />
        )}
    </FooterColumn>
);
